/**
 * Sunset Theme
 *
 * Warm orange and coral tones inspired by sunsets.
 */

import type { AppTheme } from './types';

export const sunsetTheme: AppTheme = {
  name: 'sunset',
  displayName: 'Sunset',
  description: 'Warm orange and coral accents',

  antdTheme: {
    token: {
      colorPrimary: '#ea580c',
      colorSuccess: '#65a30d',
      colorWarning: '#f59e0b',
      colorError: '#dc2626',
      colorInfo: '#f97316',
      colorLink: '#ea580c',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,
      borderRadiusXS: 4,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#fff7ed',
      colorBorder: '#fed7aa',
      colorBorderSecondary: '#ffedd5',

      colorText: '#7c2d12',
      colorTextSecondary: '#9a3412',
      colorTextTertiary: '#fdba74',
      colorTextQuaternary: '#fed7aa',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#c2410c',
        headerColor: '#ffffff',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#7c2d12',
        bodyBg: '#fff7ed',
      },

      Button: {
        borderRadius: 8,
        borderRadiusLG: 10,
        borderRadiusSM: 6,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 10,
        borderRadiusLG: 12,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#fff7ed',
        headerColor: '#fdba74',
        headerBorderRadius: 8,
        borderRadius: 8,
        rowHoverBg: '#fff7ed',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Select: {
        borderRadius: 8,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 4,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 12,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#fdba74',
        linkColor: '#ea580c',
      },

      Segmented: {
        borderRadius: 8,
        itemSelectedBg: '#ea580c',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#fdba74',
        itemSelectedColor: '#7c2d12',
        itemHoverColor: '#9a3412',
      },

      Dropdown: {
        borderRadiusLG: 8,
      },

      Tooltip: {
        borderRadius: 6,
      },

      Divider: {
        colorSplit: '#ffedd5',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#c2410c',
    '--layout-sidebar-bg': '#7c2d12',
    '--layout-content-bg': '#fff7ed',

    '--surface-0': '#fff7ed',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#7c2d12',
    '--text-secondary': '#9a3412',
    '--text-tertiary': '#fdba74',
    '--text-muted': '#fed7aa',

    '--border-default': '#fed7aa',
    '--border-subtle': '#ffedd5',
    '--border-strong': '#fdba74',

    '--border-color': '#fed7aa',
    '--border-radius': '8px',

    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    '--accent-warm': '#f59e0b',
    '--accent-cool': '#f97316',

    '--status-success': '#65a30d',
    '--status-warning': '#f59e0b',
    '--status-error': '#dc2626',
    '--status-info': '#f97316',

    '--status-success-subtle': '#f7fee7',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#fff7ed',

    '--control-bg': '#fff7ed',
    '--control-border': '#fed7aa',
    '--control-hover': '#ffedd5',

    '--hover-bg': '#fff7ed',
    '--active-bg': '#ffedd5',
  },
};
